<style scoped>
.modal.show {
	background-color: rgba(0, 0, 0, 0.5);
}
.district-option {
	cursor: pointer;
	padding: 0.5rem 0.75rem;
	border-bottom: 1px solid #eee;
	transition: background-color 0.15s ease;
}
.district-option:hover {
	background-color: #f0f7ff;
}
.district-option.active {
	background-color: #e3f0ff;
}
</style>

<template>
	<div
		class="modal fade"
		:class="{ 'show d-block': show }"
		tabindex="-1"
		v-show="show"
		@click.self="handleCancel"
	>
		<div class="modal-dialog modal-dialog-centered">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">
						<i class="bi bi-arrow-right-circle me-2"></i>Transfer Communication
					</h5>
					<button
						type="button"
						class="btn-close"
						@click="handleCancel"
					></button>
				</div>
				<div class="modal-body">
					<div class="alert alert-info" role="alert">
						<i class="bi bi-info-circle me-2"></i>
						This communication will be moved out of the current district and
						sent to another district.
					</div>

					<div class="mb-3">
						<label class="form-label fw-semibold" for="district-search"
							>Select Target District</label
						>
						<input
							id="district-search"
							type="text"
							class="form-control"
							:class="{ 'is-invalid': validationError }"
							v-model="searchQuery"
							@input="onSearchInput"
							placeholder="Type a district name..."
							ref="searchInput"
							autocomplete="off"
						/>
						<div
							v-if="validationError"
							class="invalid-feedback"
						>
							{{ validationError }}
						</div>
					</div>

					<!-- Dropdown list -->
					<div
						v-if="showDropdown && filteredDistricts.length > 0"
						class="list-group mb-3"
						style="max-height: 200px; overflow-y: auto"
					>
						<div
							v-for="d in filteredDistricts"
							:key="d.slug"
							class="district-option"
							:class="{ active: selectedDistrict?.slug === d.slug }"
							@click="selectDistrict(d)"
						>
							<div class="fw-medium">{{ d.name }}</div>
							<small class="text-muted">{{ d.slug }}</small>
						</div>
					</div>
					<div
						v-else-if="showDropdown && searchQuery && filteredDistricts.length === 0"
						class="text-muted small mb-3"
					>
						No districts match "{{ searchQuery }}"
					</div>

					<div
						v-if="selectedDistrict"
						class="selected-district bg-light p-3 rounded"
					>
						<small class="text-muted d-block">Selected District:</small>
						<span class="fw-medium">{{ selectedDistrict.name }}</span>
					</div>
				</div>
				<div class="modal-footer">
					<button
						type="button"
						class="btn btn-outline-secondary"
						@click="handleCancel"
					>
						Close Window
					</button>
					<ButtonLoading
						@click="handleTransfer"
						:disabled="!selectedDistrict"
						:loading="transferring"
						icon="bi-arrow-right-circle"
						text="Complete Transfer"
						variant="primary"
					/>
				</div>
			</div>
		</div>
	</div>
	<div
		class="modal-backdrop fade show"
		v-show="show"
		@click="handleCancel"
	></div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";
import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import { District } from "@/type/api";

interface Emits {
	(e: "close"): void;
	(e: "transferred"): void;
}
interface Props {
	communicationID: string;
	show: boolean;
}

const emit = defineEmits<Emits>();
const props = defineProps<Props>();

const searchInput = ref<HTMLInputElement | null>(null);
const searchQuery = ref("");
const districts = ref<District[]>([]);
const selectedDistrict = ref<District | null>(null);
const transferring = ref(false);
const validationError = ref("");
const showDropdown = ref(false);

const filteredDistricts = computed(() => {
	if (!searchQuery.value.trim()) return districts.value;
	const q = searchQuery.value.toLowerCase();
	return districts.value.filter(
		(d) =>
			d.name.toLowerCase().includes(q) ||
			d.slug.toLowerCase().includes(q),
	);
});

function onSearchInput() {
	showDropdown.value = true;
	selectedDistrict.value = null;
	validationError.value = "";
}

function selectDistrict(d: District) {
	selectedDistrict.value = d;
	searchQuery.value = d.name;
	showDropdown.value = false;
	validationError.value = "";
}

function handleCancel() {
	searchQuery.value = "";
	selectedDistrict.value = null;
	validationError.value = "";
	showDropdown.value = false;
	emit("close");
}

async function handleTransfer() {
	if (!selectedDistrict.value) {
		validationError.value = "Please select a district.";
		return;
	}
	if (!props.communicationID) {
		validationError.value = "No communication selected.";
		return;
	}
	transferring.value = true;
	validationError.value = "";
	try {
		await apiClient.JSONPost(
			`/api/communication/${props.communicationID}/transfer`,
			{ organization_id: selectedDistrict.value.id },
		);
		emit("transferred");
		handleCancel();
	} catch (e: any) {
		validationError.value = e?.message || "Transfer failed. Please try again.";
	} finally {
		transferring.value = false;
	}
}

async function loadDistricts() {
	try {
		const result = (await apiClient.JSONGet("/api/district")) as District[];
		districts.value = result;
	} catch (e: any) {
		console.error("Failed to load districts", e);
	}
}

watch(
	() => props.show,
	async (newVal) => {
		if (newVal) {
			await loadDistricts();
			searchQuery.value = "";
			selectedDistrict.value = null;
			validationError.value = "";
			showDropdown.value = false;
			await nextTick();
			searchInput.value?.focus();
		}
	},
);
</script>
